import { useEffect } from 'react';

/** Canonical site URL (apex — no www). */
export const SITE_ORIGIN = 'https://homeandown.com';

interface MetaTagConfig {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

const PAGE_META_DATA: Record<string, MetaTagConfig> = {
  '/': {
    title: 'Best Real Estate Company in Hyderabad | Home and Own',
    description: 'Best real estate company in Hyderabad — Home and Own offers expert guidance for buying, selling, renting, and managing properties—find your dream home!',
    keywords: 'best real estate company in hyderabad, property in hyderabad, homes for sale hyderabad, rent property hyderabad',
    canonical: `${SITE_ORIGIN}/`,
  },
  '/buy': {
    title: 'Buy Property in Hyderabad — Verified Listings | Home and Own',
    description: 'Looking to buy property in Hyderabad? Visit Home and Own and explore verified listings, expert support, and the best homes in prime locations across the city.',
    keywords: 'buy property in hyderabad, homes for sale hyderabad, property for purchase hyderabad',
    canonical: `${SITE_ORIGIN}/buy`,
  },
  '/rent': {
    title: 'Property for Rent in Hyderabad — Flats & Homes | Home and Own',
    description: 'Find the perfect property for rent in Hyderabad at Home and Own. Includes verified listings, flexible options, and expert assistance for hassle-free renting.',
    keywords: 'property for rent in hyderabad, apartments for rent hyderabad, flats for rent hyderabad',
    canonical: `${SITE_ORIGIN}/rent`,
  },
  '/sell': {
    title: 'Property for Sale in Hyderabad — Get Best Value | Home and Own',
    description:
      'List your property for sale in Hyderabad at Home and Own, with expert pricing, marketing support, and serious buyers to ensure a fast, profitable deal.',
    keywords: 'sell property in hyderabad, property for sale in hyderabad, list property hyderabad, home selling hyderabad',
    canonical: `${SITE_ORIGIN}/sell`,
  },
  '/property-management': {
    title: 'Property Management Companies in Hyderabad | Home and Own',
    description: 'Choose Home and Own, one of the leading property management companies in Hyderabad, for tenant handling, maintenance, and complete property care services.',
    keywords: 'property management companies in hyderabad, property management services hyderabad',
    canonical: `${SITE_ORIGIN}/property-management`,
  },
  '/agent': {
    title: 'Real Estate Agents in Hyderabad — Trusted Pros | Home and Own',
    description: 'Find trusted real estate agents in Hyderabad at Home and Own for buying, selling, or renting properties with expert guidance and personalized support.',
    keywords: 'real estate agents in hyderabad, property agents hyderabad, real estate professionals',
    canonical: `${SITE_ORIGIN}/agent`,
  },
  '/contact-us': {
    title: 'Real Estate Companies in Hyderabad | Contact Home and Own',
    description: 'Contact Home and Own, one of the trusted real estate companies in Hyderabad, for expert guidance with buying, selling, or renting property across India.',
    keywords: 'real estate companies in hyderabad, property companies hyderabad, real estate services',
    canonical: `${SITE_ORIGIN}/contact-us`,
  },
};

/** Routes that should not inject SEO meta (canonical, description, keywords, og:url). */
const NO_SEO_META_PATHS = new Set(['/host']);

function stripSeoMetaFromHeadForHostPath() {
  document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
  document.querySelectorAll('meta[name="description"]').forEach((el) => el.remove());
  document.querySelectorAll('meta[name="keywords"]').forEach((el) => el.remove());
  document.querySelectorAll('meta[property="og:url"]').forEach((el) => el.remove());
  document.title = 'Home and Own';
}

function canonicalForPath(pathname: string): string {
  const normalized = (pathname || '/').replace(/\/+$/, '') || '/';
  if (normalized === '/') return `${SITE_ORIGIN}/`;
  return `${SITE_ORIGIN}${normalized.startsWith('/') ? normalized : `/${normalized}`}`;
}

function metaPathKey(pathname: string): string {
  return (pathname || '/').replace(/\/+$/, '') || '/';
}

/**
 * Hook to update meta tags based on the current route
 * Usage: useMetaTags('/buy') in your page component
 */
export function useMetaTags(pathname: string, customMeta?: MetaTagConfig) {
  useEffect(() => {
    const pathKey = metaPathKey(pathname);

    if (!customMeta && NO_SEO_META_PATHS.has(pathKey)) {
      stripSeoMetaFromHeadForHostPath();
      return;
    }

    const meta =
      customMeta ||
      PAGE_META_DATA[pathKey] || {
        ...PAGE_META_DATA['/'],
        canonical: canonicalForPath(pathname),
      };

    // Update title
    if (meta.title) {
      document.title = meta.title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      let element = document.querySelector(property ? `meta[property="${name}"]` : `meta[name="${name}"]`) as HTMLMetaElement | null;

      if (!element) {
        element = document.createElement('meta');
        if (property) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }

      element.content = content;
    };

    // Update description
    if (meta.description) {
      updateMetaTag('description', meta.description);
    }

    // Social crawlers often read og:/twitter: tags — keep them in sync with title/description when not overridden
    if (meta.description) {
      if (!meta.ogDescription) {
        updateMetaTag('og:description', meta.description, true);
      }
      if (!meta.twitterDescription) {
        updateMetaTag('twitter:description', meta.description);
      }
    }

    // Update keywords
    if (meta.keywords) {
      updateMetaTag('keywords', meta.keywords);
    }

    // Update canonical (and match Open Graph URL for consistency on every route)
    if (meta.canonical) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = meta.canonical;
      updateMetaTag('og:url', meta.canonical, true);
    }

    // Update Open Graph tags
    if (meta.ogTitle) {
      updateMetaTag('og:title', meta.ogTitle, true);
    } else if (meta.title) {
      updateMetaTag('og:title', meta.title, true);
    }
    if (meta.ogDescription) {
      updateMetaTag('og:description', meta.ogDescription, true);
    }
    if (meta.ogUrl) {
      updateMetaTag('og:url', meta.ogUrl, true);
    }
    if (meta.ogImage) {
      updateMetaTag('og:image', meta.ogImage, true);
    }

    // Update Twitter Card tags
    if (meta.twitterTitle) {
      updateMetaTag('twitter:title', meta.twitterTitle);
    } else if (meta.title) {
      updateMetaTag('twitter:title', meta.title);
    }
    if (meta.twitterDescription) {
      updateMetaTag('twitter:description', meta.twitterDescription);
    }
    if (meta.twitterImage) {
      updateMetaTag('twitter:image', meta.twitterImage);
    }
  }, [pathname, customMeta]);
}

/**
 * Alternative: Use this selector to automatically update meta tags
 * Call this in your main App.tsx with the current location
 */
export function useAutoMetaTags() {
  const pathname = window.location.pathname;
  useMetaTags(pathname);
}
